import { AlertFactory, AlertState } from 'alerts/Alert'
import { FindMembers } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import ModalViewModel from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { CursorPaged, CursorPageQuery, Id } from 'types/definitions'
import { maxLength } from 'utils/validation-utils'
import { required, validationContext } from 'validx'
import { BehaviorSubject } from 'rxjs'
import { CancellationToken } from 'infra/cancellation'
import ProviderStore from 'jobs/ProviderStore'
import { CollaboratorType } from 'jobs/dtos/job'
import { FindTeamMembers } from 'iam/TeamMemberStore'
import { GetCurrentMember } from 'iam/SessionStore'
import { UserDataType } from 'iam/models/UserDataType'

/**
 * View Model for the Add Collaborators as Admin dialog.
 * @extends ModalViewModel
 */
export default class AddCollaboratorsAsAdminViewModel extends ModalViewModel {
  /**
   * Tracks whether the view model is active.
   */
  private readonly _active = new BehaviorSubject<boolean>(false)

  /**
   * Max reason length.
   */
  readonly MAX_REASON_LENGTH = 100 as const

  /**
   * Collaborator to add.
   */
  @observable collaborator?: Member | null = undefined

  /**
   * The type of collaborator we are adding to the job
   */
  @observable collaboratorType: CollaboratorType = 'PROVIDER'

  /**
   * Disables submit button.
   */
  @observable disabled = true

  /**
   * The reason for adding the collaborator.
   */
  @observable reason = ''

  /**
   * Whether we can display all pros.
   */
  @observable
  canDisplayAllPros = false

  /**
   * Whether we can display qualified pros.
   */
  @observable
  canDisplayQualifiedPros = true

  /**
   * Helper text for reason input field.
   */
  @computed
  get reasonHelpText() {
    if (this.isAddingClient) {
      return 'Why are you adding another Client? This text is public and can be seen by the Client and all Pros.'
    }

    return this.modalType === 'champion'
      ? 'This text is public and can be seen by the Client and all Pros.'
      : 'Why are you adding another Pro? This text is public and can be seen by the Client and all Pros.'
  }

  @computed
  get proSelectionHelpText() {
    return this.modalType === 'requestingChampion'
      ? 'The selected Pro will receive a request and will be added to the job upon approval.'
      : undefined
  }

  /**
   * Validation context for View Model.
   */
  readonly validation = validationContext(this, {
    collaborator: [required('Collaborator is required')],
    reason: [
      maxLength(
        this.MAX_REASON_LENGTH,
        `Reason must be less than ${this.MAX_REASON_LENGTH} characters.`,
      ),
    ],
  })

  constructor(
    private job: Job,
    private jobStore: JobStore,
    private findMembers: FindMembers,
    private findTeamMembers: FindTeamMembers,
    private providerStore: ProviderStore,
    private newAlert: AlertFactory,
    private getCurrentMember: GetCurrentMember,
  ) {
    super()

    makeObservable(this)

    if (this.isDraftJob || this.canOnlyAddClient) {
      this.setAddingClient()
    }

    this.close$.subscribe(this.reset)
    this.show$.subscribe(this.shown)
  }

  @computed
  get isAddingPro() {
    return this.collaboratorType === 'PROVIDER'
  }

  @computed
  get isAddingClient() {
    return this.collaboratorType === 'CLIENT'
  }

  @computed
  get isClientTeamJob() {
    return this.job.isClientTeamJob
  }

  @computed
  get isDraftJob() {
    return this.job.isDraft
  }

  @computed
  get canOnlyAddClient() {
    return (
      !this.getCurrentMember(this.job.currentMemberWorkspaceId).hasPermission(
        'ADMIN_USER_LIST',
      ) &&
      this.getCurrentMember(this.job.currentMemberWorkspaceId).hasPermission(
        'ADMIN_CLIENT_USER_LIST',
      )
    )
  }

  /**
   * Modal type when adding a Pro.
   */
  @computed
  get modalType() {
    if (this.job.status === 'UNCLAIMED') {
      return 'champion'
    }

    if (this.job.status === 'INFO_GATHERING') {
      return 'requestingChampion'
    }

    return 'supporting'
  }

  /**
   * Reason input characters remaining text label.
   */
  @computed
  get reasonCharsRemaining() {
    return this.reason
      ? this.MAX_REASON_LENGTH - this.reason.length
      : this.MAX_REASON_LENGTH
  }

  /**
   * Shows the Add Pro as Admin Dialog.
   */
  show() {
    if (this.showing) {
      return
    }

    super.show()
  }

  /**
   * Sets dropdown to show all pros.
   */
  @action.bound
  setDisplayAllPros() {
    this.canDisplayAllPros = true
    this.canDisplayQualifiedPros = false
  }

  /**
   * Sets dropdown to show only qualified pros.
   */
  @action.bound
  setDisplayQualifiedPros() {
    this.canDisplayAllPros = false
    this.canDisplayQualifiedPros = true
  }

  /**
   * Sets a collaborator to add.
   */
  @action.bound
  setCollaborator(collaborator?: Member | null) {
    if (this.collaborator === collaborator) {
      return
    }
    this.collaborator = collaborator
    this.disabled = !this.validate()
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.collaborator = undefined
    this.reason = ''
    this.disabled = true
    this.canDisplayAllPros = false
    this.canDisplayQualifiedPros = true

    this.submit.reset()
    this.validation.reset()
    this._active.next(false)
  }

  /**
   * Activates the member search field with a debounce time.
   */
  @action.bound
  shown() {
    this._active.next(true)
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Sets the collaborator type we are adding to Pro.
   */
  @action.bound
  setAddingPro() {
    this.collaborator = undefined
    this.collaboratorType = 'PROVIDER'
  }

  /**
   * Sets the collaborator type we are adding to Client.
   */
  @action.bound
  setAddingClient() {
    this.collaborator = undefined
    this.collaboratorType = 'CLIENT'
  }

  /**
   * Adds the champion Pro as Admin.
   */
  @action.bound
  async addChampionProviderAsAdmin() {
    const alert = this.newAlert().withState(AlertState.Pending).show()

    if (!this.job.canAddChampionProviderAsAdmin) {
      alert
        .withState(AlertState.Error)
        .saying('You do not have permission to perform this action.')
        .autoDismiss()
      return
    }
    if (!this.validate()) {
      return
    }

    alert
      .withState(AlertState.Pending)
      .saying(
        'Adding Provider',
        `Adding ${this.collaborator?.displayName} to ${this.job.displayName}...`,
      )
      .autoDismiss()
      .show()

    await this.jobStore.addChampionProviderAsAdmin(
      Id.assert(this.job.id),
      Id.assert(this.collaborator?.user.publicId),
      this.reason,
    )

    alert
      .withState(AlertState.Success)
      .saying(
        'Success!',
        `${this.collaborator?.displayName} was added to ${this.job.displayName}.`,
      )
      .autoDismiss()

    this.close()
  }

  /**
   * Adds the supporting Pro as Admin.
   */
  @action.bound
  async addSupportingProviderAsAdmin() {
    const alert = this.newAlert().withState(AlertState.Pending).show()

    if (!this.job.canAddSupportingProviderAsAdmin) {
      alert
        .withState(AlertState.Error)
        .saying('You do not have permission to perform this action.')
        .autoDismiss()
      return
    }
    if (!this.validate()) {
      return
    }

    alert
      .withState(AlertState.Pending)
      .saying(
        'Adding Provider',
        `Adding ${this.collaborator?.displayName} to ${this.job.displayName}...`,
      )
      .autoDismiss()
      .show()

    await this.jobStore.addSupportingProviderAsAdmin(
      Id.assert(this.job.id),
      Id.assert(this.collaborator?.user.publicId),
      this.reason,
    )

    alert
      .withState(AlertState.Success)
      .saying(
        'Success!',
        `${this.collaborator?.displayName} was added to ${this.job.displayName}.`,
      )
      .autoDismiss()

    this.close()
  }

  /**
   * Adds the supporting Pro as Admin.
   */
  @action.bound
  async requestChampionProviderAsAdmin() {
    const alert = this.newAlert().withState(AlertState.Pending).show()

    if (!this.job.canRequestChampionProviderAsAdmin) {
      alert
        .withState(AlertState.Error)
        .saying('You do not have permission to perform this action.')
        .autoDismiss()
      return
    }

    if (!this.validate()) {
      return
    }

    alert
      .withState(AlertState.Pending)
      .saying(
        'Requesting Provider',
        `Requesting ${this.collaborator?.displayName} to ${this.job.displayName}...`,
      )
      .show()

    await this.jobStore.requestChampionProviderAsAdmin(
      Id.assert(this.job.id),
      Id.assert(this.collaborator?.user.publicId),
    )

    alert
      .withState(AlertState.Success)
      .saying(
        'Success!',
        `${this.collaborator?.displayName} was requested to ${this.job.displayName}.`,
      )
      .autoDismiss()

    this.close()
  }

  /**
   * Adds the supporting Client as Admin.
   */
  @action.bound
  async addSupportingClientAsAdmin() {
    const alert = this.newAlert().withState(AlertState.Pending).show()

    if (!this.job.canAddSupportingClientAsAdmin) {
      alert
        .withState(AlertState.Error)
        .saying('You do not have permission to perform this action.')
        .autoDismiss()
      return
    }
    if (!this.validate()) {
      return
    }

    alert
      .withState(AlertState.Pending)
      .saying(
        'Adding Client',
        `Adding ${this.collaborator?.displayName} to ${this.job.displayName}...`,
      )
      .autoDismiss()
      .show()

    await this.jobStore.addSupportingClientAsAdmin(
      Id.assert(this.job.id),
      Id.assert(this.collaborator?.user.publicId),
      this.reason,
    )

    alert
      .withState(AlertState.Success)
      .saying(
        'Success!',
        `${this.collaborator?.displayName} was added to ${this.job.displayName}.`,
      )
      .autoDismiss()

    this.close()
  }

  /**
   * submits the new collaborator depending on the type and role.
   */
  submit = task.resolved(async () => {
    if (this.isAddingClient) {
      return this.addSupportingClientAsAdmin()
    }

    if (this.modalType === 'champion') {
      return this.addChampionProviderAsAdmin()
    }

    if (this.modalType === 'supporting') {
      return this.addSupportingProviderAsAdmin()
    }

    return this.requestChampionProviderAsAdmin()
  })

  /**
   * Fetches collaborators.
   */
  fetchWorkspaceMembers = async (
    searchText: string,
    params: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<Member>> => {
    const collaborators = this.job.collaborators
    const paged = await this.findMembers(
      {
        after: params.after,
        limit: params.limit,
        workspace_id: this.job.currentMemberWorkspaceId,
        search_text: searchText,
        user_types: this.canOnlyAddClient ? [UserDataType.User] : undefined,
      },
      cancellationToken,
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((m) => {
        const invalidCandidate =
          collaborators.some((s) => s.member === m) ||
          m.user.id === 'SYSTEM_ROBOT'
        return !invalidCandidate
      }),
    )
  }

  /**
   * Fetches qualified pros.
   */
  fetchQualifiedPros = async (
    searchText: string,
    query: CursorPageQuery,
  ): Promise<CursorPaged<Member | null>> => {
    return this.providerStore.fetchQualifiedProviders(
      searchText,
      query,
      this.job,
    )
  }

  /**
   * Fetches team members.
   */
  fetchTeamMembers = async (
    _searchText: string,
    params: CursorPageQuery,
  ): Promise<CursorPaged<Member | null>> => {
    if (!this.job.ownerTeam) {
      return CursorPaged.empty
    }

    const collaborators = this.job.collaborators
    const paged = await this.findTeamMembers({
      after: params.after,
      limit: params.limit,
      team_id: Id.assert(this.job.ownerTeam.id),
    })

    return CursorPaged.map(paged, (data) =>
      data
        .filter((m) => {
          const invalidCandidate = collaborators.some(
            (s) => s.member === m.member,
          )
          return !invalidCandidate
        })
        .map((m) => m.member),
    )
  }
}
